import { Injectable, OnDestroy } from "@angular/core";
import { BehaviorSubject, Subject, interval } from "rxjs";
import { filter, switchMap, take, takeUntil, takeWhile } from "rxjs/operators";
import { InspectionImageListItem } from "../_objects/inspection-image";
import { InspectionImageService } from "../_services/http-api/inspection-image.service";
import { INSPECTION_IMAGE_STATUS_CODE } from "../app.constants";
import { InspectionImageFilters } from "../_objects/inspection-image-filters";
import { ImagesFiltersStateService } from "../_services/filter-state.service";

@Injectable({
  providedIn: 'root'
})
export class InspectionImageStateService implements OnDestroy {
  private inspectionImagesSubject = new BehaviorSubject<InspectionImageListItem[] | null>(null);
  public inspectionImages$ = this.inspectionImagesSubject.asObservable().pipe(filter(images => images != null));

  private onDestroy$ = new Subject();
  private MAX_RETRY_COUNT = 100;
  private pollingInterval = 5000; // 5 seconds
  
  constructor(private inspectionImageService: InspectionImageService, private filterStateService: ImagesFiltersStateService) { }

  ngOnDestroy(): void {
    this.onDestroy$.next(undefined);
    this.onDestroy$.unsubscribe();
  }

  public fetchAndUpdate(inspectionId: string) {
    const filters = this.filterStateService.inspectionImageFilters;
    this.inspectionImageService
      .getInspectionImages(inspectionId, filters)
      .subscribe({
        next: (inspectionImageList: InspectionImageListItem[]) => {
          this.inspectionImagesSubject.next(inspectionImageList);
          if (inspectionImageList.some(inspectionImage => inspectionImage.status == INSPECTION_IMAGE_STATUS_CODE.UPLOADING)) {
            this.pollTillAllUploaded(inspectionId, filters);
          }
        },
      });
  }

  private imagesStillUploading(inspectionImageList: InspectionImageListItem[]) {
    return inspectionImageList.some(inspectionImage => inspectionImage.status != INSPECTION_IMAGE_STATUS_CODE.COMPLETED);
  }

  private pollTillAllUploaded(inspectionId: string, filters: InspectionImageFilters) {
    return interval(this.pollingInterval)
      .pipe(
        switchMap(() => this.inspectionImageService.getInspectionImages(inspectionId, filters)),
        take(this.MAX_RETRY_COUNT),
        takeUntil(this.onDestroy$),
        takeWhile((images) => this.imagesStillUploading(images), true),
      ).subscribe({
        next: (inspectionImageList: InspectionImageListItem[]) => {
          this.inspectionImagesSubject.next(inspectionImageList);
        },
        complete: () => {
          this.setStillUploadingImageToError(this.inspectionImagesSubject.value ?? []);
        }
      });
  }

  private setStillUploadingImageToError(inspectionImageList: InspectionImageListItem[]) {
    const imagesUpdated = inspectionImageList.map(inspectionImages => {
      return inspectionImages.status === INSPECTION_IMAGE_STATUS_CODE.UPLOADING ?
        { ...inspectionImages, status: INSPECTION_IMAGE_STATUS_CODE.ERROR } : inspectionImages;
    });
    this.inspectionImagesSubject.next(imagesUpdated);
  }

  public clearState() {
    this.inspectionImagesSubject.next(null);
    this.onDestroy$.next(null);
  }
}
